/**
 * React Query hooks for user delegation
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getDelegates, addDelegate, removeDelegate } from '@/lib/api/users';
import { userKeys } from '@/lib/query-keys';

/**
 * Fetch delegates for a user
 */
export function useDelegates(userId: number) {
  return useQuery({
    queryKey: userKeys.delegates(userId),
    queryFn: () => getDelegates(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Add delegate mutation
 */
export function useAddDelegate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, delegatedToUserId }: { userId: number; delegatedToUserId: number }) =>
      addDelegate(userId, delegatedToUserId),
    onSuccess: () => {
      // Invalidate all user-related queries to refresh delegation data
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

/**
 * Remove delegate mutation
 */
export function useRemoveDelegate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, delegateId }: { userId: number; delegateId: number }) =>
      removeDelegate(userId, delegateId),
    onSuccess: () => {
      // Invalidate all user-related queries to refresh delegation data
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}
